import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Client } from '../models/Client';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

export function useClients(organizationId: string | null) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  const canManageProjects =
    currentUser?.role === UserRole.SUPER_ADMIN ||
    currentUser?.role === UserRole.CLIENT_ADMIN;

  useEffect(() => {
    setLoading(true);

    let q;
    let needsSorting = false;
    let unsubscribeLegacy: (() => void) | null = null;
    if (!organizationId) {
      // If no organizationId and user is SUPER_ADMIN, query all projects
      if (currentUser?.role === UserRole.SUPER_ADMIN) {
        q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
        // No sorting needed - Firestore handles it
      } else {
        setClients([]);
        setLoading(false);
        return;
      }
    } else {
      // organizationId param is the top-level clientId in the new model.
      q = query(
        collection(db, 'projects'),
        where('clientId', '==', organizationId)
      );
      needsSorting = true;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty && organizationId) {
          if (!unsubscribeLegacy) {
            // Legacy fallback: projects lived in /clients with organizationId.
            unsubscribeLegacy = onSnapshot(
              query(collection(db, 'clients'), where('organizationId', '==', organizationId)),
              (legacySnapshot) => {
                const legacyProjects = legacySnapshot.docs.map((doc) => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    ...data,
                    entityType: data.entityType || 'project_legacy',
                    clientId: data.clientId || data.organizationId || '',
                    organizationId: data.organizationId || data.clientId || '',
                    createdAt: data.createdAt?.toDate() || new Date(),
                    updatedAt: data.updatedAt?.toDate() || new Date(),
                  } as Client;
                });
                legacyProjects.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
                setClients(legacyProjects);
                setLoading(false);
                setError(null);
              },
              (err) => {
                console.error('Error fetching legacy projects:', err);
                setError(err as Error);
                setLoading(false);
              }
            );
          }
          return;
        }

        if (unsubscribeLegacy) {
          unsubscribeLegacy();
          unsubscribeLegacy = null;
        }

        const clientsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            entityType: data.entityType || 'project',
            clientId: data.clientId || data.organizationId || '',
            organizationId: data.organizationId || data.clientId || '',
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Client;
        });

        const accessibleProjectIds = new Set([
          ...Object.keys(currentUser?.projectAccess || {}),
          ...Object.keys(currentUser?.clientAccess || {}),
        ]);

        const visibleClientsData =
          currentUser?.role === UserRole.PROJECT_ADMIN
            ? clientsData.filter((client) => accessibleProjectIds.has(client.id))
            : clientsData;
        
        // Sort by createdAt descending (newest first) if we removed orderBy
        if (needsSorting) {
          visibleClientsData.sort((a, b) => {
            const aTime = a.createdAt.getTime();
            const bTime = b.createdAt.getTime();
            return bTime - aTime; // Descending order
          });
        }
        
        setClients(visibleClientsData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        console.error('Error fetching clients:', err);
        // Check if it's an index building error
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      if (unsubscribeLegacy) unsubscribeLegacy();
    };
  }, [organizationId, currentUser?.role, currentUser?.projectAccess, currentUser?.clientAccess]);

  // Helper function to convert Date objects to Timestamps recursively
  const convertDatesToTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (Array.isArray(obj)) return obj.map(convertDatesToTimestamps);
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          converted[key] = convertDatesToTimestamps(obj[key]);
        }
      }
      return converted;
    }
    return obj;
  };

  const createClient = async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!canManageProjects) {
        throw new Error('Only client admins and super admins can create projects');
      }
      const now = Timestamp.now();
      // Convert any Date objects to Timestamps
      const convertedData = convertDatesToTimestamps(clientData);
      await addDoc(collection(db, 'projects'), {
        ...convertedData,
        entityType: 'project',
        // Keep legacy mirrors during transition
        organizationId: convertedData.organizationId || convertedData.clientId || '',
        clientId: convertedData.clientId || convertedData.organizationId || '',
        projectId: null,
        createdAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('Error creating client:', err);
      throw err;
    }
  };

  const updateClient = async (clientId: string, updates: Partial<Client>) => {
    try {
      if (!canManageProjects) {
        throw new Error('Only client admins and super admins can update projects');
      }
      const clientRef = doc(db, 'projects', clientId);
      const relationUpdates: Record<string, unknown> = {};
      const resolvedClientId = updates.clientId || updates.organizationId;
      if (resolvedClientId !== undefined) {
        relationUpdates.clientId = resolvedClientId;
        relationUpdates.organizationId = resolvedClientId;
      }
      await updateDoc(clientRef, {
        ...updates,
        ...relationUpdates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating client:', err);
      throw err;
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      if (!canManageProjects) {
        throw new Error('Only client admins and super admins can delete projects');
      }
      const clientRef = doc(db, 'projects', clientId);
      await deleteDoc(clientRef);
    } catch (err) {
      console.error('Error deleting client:', err);
      throw err;
    }
  };

  return {
    clients,
    loading,
    error,
    createClient,
    updateClient,
    deleteClient,
  };
}
